import { useEffect, useRef, useState } from 'react';
import {
  createComment,
  listAccountUsernames,
  listComments,
  type RelayConfig,
} from '../api/client';
import type { CommentTask } from '../api/types';
import { StatusPill } from '../components/StatusPill';

interface Props {
  cfg: RelayConfig;
}

/**
 * Compose a comment: pick an account, paste a Reddit post URL, write the
 * body, submit. The browser can't post directly (that needs the account's
 * Playwright session on the consumer host), so submitting creates a KV task
 * the consumer's `drain-comments` posts asynchronously — the list below
 * reflects pending → posted (permalink) / failed (reason).
 */
export function Comment({ cfg }: Props) {
  const [accounts, setAccounts] = useState<string[]>([]);
  const [account, setAccount] = useState('');
  const [url, setUrl] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tasks, setTasks] = useState<CommentTask[]>([]);

  // Poll the task list so submitted comments flip pending → posted/failed
  // without a manual refresh.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    loadAccounts();
    refreshTasks();
    pollRef.current = setInterval(refreshTasks, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token]);

  async function loadAccounts() {
    try {
      const r = await listAccountUsernames(cfg);
      const sorted = [...r.usernames].sort((a, b) => a.localeCompare(b));
      setAccounts(sorted);
      setAccount((cur) => cur || sorted[0] || '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function refreshTasks() {
    try {
      setTasks(await listComments(cfg, 30));
    } catch {
      // transient — keep the last good list, the next poll retries
    }
  }

  async function submit() {
    setErr(null);
    if (!account) return setErr('pick an account');
    if (!/^https?:\/\/(www\.|old\.|np\.)?reddit\.com\/.+/i.test(url.trim())) {
      return setErr('enter a reddit.com post link');
    }
    if (!body.trim()) return setErr('comment body is empty');
    setBusy(true);
    try {
      await createComment(cfg, { account, url: url.trim(), body });
      setToast('Queued — it will post within a couple of minutes');
      setBody('');
      setUrl('');
      await refreshTasks();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 4000);
    }
  }

  const inputCls =
    'w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-bg)] px-3 py-2 text-sm text-zinc-200 placeholder:text-[var(--color-text-faint)] focus:border-zinc-600 focus:outline-none';

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Comment</h1>
        <p className="mt-1 text-sm text-[var(--color-text-dim)]">
          Post a comment as one of your accounts. Submitting queues the task;
          the consumer posts it with full anti-bot pacing (a couple of minutes)
          and the status shows below.
        </p>
      </header>

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      <section className="card overflow-hidden">
        <div className="border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            New comment
          </span>
        </div>
        <div className="space-y-4 p-4">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,220px)_1fr]">
            <label className="space-y-1">
              <span className="text-xs text-[var(--color-text-dim)]">Account</span>
              <select
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                className={inputCls}
              >
                {accounts.length === 0 && <option value="">loading…</option>}
                {accounts.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs text-[var(--color-text-dim)]">
                Reddit post URL
              </span>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                spellCheck={false}
                placeholder="https://www.reddit.com/r/…/comments/…/…/"
                className={inputCls}
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs text-[var(--color-text-dim)]">
              Comment (Markdown)
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your comment…"
              className={`${inputCls} font-mono`}
            />
          </label>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[var(--color-text-faint)]">
              {body.length}/10000
            </span>
            <button
              onClick={submit}
              disabled={busy || !account}
              className="btn-primary"
            >
              {busy ? 'Queuing…' : 'Post comment'}
            </button>
          </div>
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            Recent
          </span>
          <span className="text-xs text-[var(--color-text-faint)]">
            {tasks.length} task(s)
          </span>
        </div>
        {tasks.length === 0 ? (
          <div className="px-4 py-6 text-sm text-[var(--color-text-faint)]">
            No comments submitted yet.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {tasks.map((t) => (
              <li key={t.id} className="space-y-1 px-4 py-3">
                <div className="flex items-center gap-2">
                  <StatusPill status={t.status} />
                  <span className="text-sm font-medium text-zinc-200">
                    u/{t.account}
                  </span>
                  <span className="ml-auto text-xs text-[var(--color-text-faint)]">
                    {fmtTime(t.created_at)}
                  </span>
                </div>
                <div className="truncate text-xs text-[var(--color-text-dim)]">
                  {t.body}
                </div>
                <div className="flex flex-wrap gap-x-3 text-xs">
                  <a
                    href={t.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sky-400 hover:underline"
                  >
                    target post ↗
                  </a>
                  {t.status === 'succeeded' && t.permalink && (
                    <a
                      href={t.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-emerald-400 hover:underline"
                    >
                      posted comment ↗
                    </a>
                  )}
                  {t.status === 'failed' && t.error && (
                    <span className="text-rose-400">{t.error}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
